import { Button, Input, Modal } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import styles from "./RegistrationGate.module.css";

/** Which list the modal is adding to — drives copy, validation + error mapping. */
export type RegistrationGateListKind = "blocklist" | "allowlist";

type ModalCopy = {
  title: string;
  valueLabel: string;
  valuePlaceholder: string;
  submitLabel: string;
  submittingLabel: string;
  /** Small note under the value input (allow-list is normalised on write). */
  valueHelperText?: string;
  /** The `details.reason` the backend sends on a 422 for this list. */
  invalidReason: string;
  invalidCopy: string;
  duplicateCopy: string;
};

const COPY: Record<RegistrationGateListKind, ModalCopy> = {
  blocklist: {
    title: "Add blocked domain",
    valueLabel: "Domain",
    valuePlaceholder: "e.g. example.com",
    submitLabel: "Add domain",
    submittingLabel: "Adding...",
    invalidReason: "invalid_domain",
    invalidCopy: "That doesn't look like a valid domain. Enter a bare domain, e.g. example.com.",
    duplicateCopy: "That domain is already on the blocklist.",
  },
  allowlist: {
    title: "Add allow-list term",
    valueLabel: "Term",
    valuePlaceholder: "e.g. scunthorpe",
    submitLabel: "Add term",
    submittingLabel: "Adding...",
    valueHelperText:
      "Terms are stored in a normalised form, so the saved value may differ slightly from what you type.",
    invalidReason: "invalid_term",
    invalidCopy: "That term was rejected. Use letters only.",
    duplicateCopy: "That term is already on the allow-list.",
  },
};

/**
 * Client-side validation — UX convenience only (lesson #4). The server is
 * authoritative and its 422 is always allowed to surface; never block a submit
 * on a rule the backend does not enforce.
 */
export function validateRegistrationGateValue(
  kind: RegistrationGateListKind,
  raw: string,
): string | null {
  const value = raw.trim();
  const label = COPY[kind].valueLabel;
  if (value.length === 0) return `${label} is required.`;
  if (kind === "blocklist") {
    // Mirror the backend `normaliseDomain`: no whitespace, no `@`, must have a dot.
    if (/\s/.test(value)) return "A domain cannot contain spaces.";
    if (value.includes("@")) return "Enter a domain only, without an @ or email address.";
    if (!value.includes(".")) return "Enter a valid domain including a dot, e.g. example.com.";
  }
  return null;
}

/** The shape client.ts attaches to a thrown API error. */
type ClientApiError = Error & {
  status?: number;
  code?: string;
  details?: Array<{ code: string; path: (string | number)[]; message: string }>;
  detailsRaw?: unknown;
};

/** Pull a `reason` string off the raw (object) `details` a 422 carries. */
function readDetailsReason(detailsRaw: unknown): string | undefined {
  if (detailsRaw && typeof detailsRaw === "object" && "reason" in detailsRaw) {
    const reason = (detailsRaw as { reason?: unknown }).reason;
    return typeof reason === "string" ? reason : undefined;
  }
  return undefined;
}

/**
 * Map a thrown API error to inline copy (lesson #2). Reads `.status`:
 * - 409 (duplicate) → friendly "already exists".
 * - 422 → friendly copy keyed off `error.detailsRaw.reason` (an OBJECT, not the
 *   `.details` array), falling back to the server message.
 * - anything else → generic message.
 */
export function getAddErrorMessage(error: unknown, kind: RegistrationGateListKind): string {
  const copy = COPY[kind];
  if (!(error instanceof Error)) return "Something went wrong. Please try again.";
  const apiError = error as ClientApiError;

  if (apiError.status === 409) {
    return copy.duplicateCopy;
  }

  if (apiError.status === 422) {
    const reason = readDetailsReason(apiError.detailsRaw);
    if (reason === copy.invalidReason) return copy.invalidCopy;
    return apiError.message || copy.invalidCopy;
  }

  return apiError.message || "Something went wrong. Please try again.";
}

export type RegistrationGateAddModalProps<T> = {
  isOpen: boolean;
  /** Which list is being added to — selects copy/validation/error mapping. */
  kind: RegistrationGateListKind;
  /** Uniform add fn wrapping the Phase 1 service; resolves the created row. */
  add: (input: { value: string; reason?: string }) => Promise<T>;
  /** Called with the created row (its stored value) on a successful add. */
  onSuccess: (createdRow: T) => void;
  onClose: () => void;
};

/**
 * Reusable "Add" modal for both Registration Gate sections. Client-side validates the
 * value (UX only), submits via the injected `add`, and on error keeps the modal
 * open with an inline message (409 duplicate / 422 invalid / other).
 */
export function RegistrationGateAddModal<T>({
  isOpen,
  kind,
  add,
  onSuccess,
  onClose,
}: RegistrationGateAddModalProps<T>) {
  const copy = COPY[kind];
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset the form whenever the modal closes (mirrors CreateUserModal).
  useEffect(() => {
    if (!isOpen) {
      setValue("");
      setReason("");
      setFieldError(null);
      setServerError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (isSubmitting) return;

    const validationError = validateRegistrationGateValue(kind, value);
    if (validationError) {
      setFieldError(validationError);
      return;
    }
    setFieldError(null);
    setServerError(null);
    setIsSubmitting(true);

    try {
      const trimmedReason = reason.trim();
      const created = await add({
        value: value.trim(),
        reason: trimmedReason.length > 0 ? trimmedReason : undefined,
      });
      onSuccess(created);
    } catch (err) {
      // Keep the modal open so the user can correct + retry (lesson #2).
      setServerError(getAddErrorMessage(err, kind));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={copy.title} size="small">
      <form onSubmit={handleSubmit} className={styles.addForm}>
        {serverError && (
          <div className={styles.serverError} role="alert" aria-live="assertive">
            {serverError}
          </div>
        )}

        <Input
          label={copy.valueLabel}
          value={value}
          onChange={event => setValue(event.target.value)}
          error={fieldError ?? undefined}
          helperText={copy.valueHelperText}
          placeholder={copy.valuePlaceholder}
          disabled={isSubmitting}
          required
          fullWidth
          autoComplete="off"
          autoFocus
        />

        <Input
          label="Reason (optional)"
          value={reason}
          onChange={event => setReason(event.target.value)}
          disabled={isSubmitting}
          fullWidth
          autoComplete="off"
        />

        <div className={styles.formActions}>
          <Button
            variant="outlined"
            color="default"
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button variant="contained" color="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? copy.submittingLabel : copy.submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default RegistrationGateAddModal;
